import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { RouterModule } from '@angular/router';
import { AuthService } from '../../../features/auth/services/auth.service';
import { User } from '@angular/fire/auth';
import { Observable, from, of, combineLatest } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { UserStateService } from '../../../core/services/user-state.service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-user-info',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatDividerModule,
    MatIconModule,
    MatListModule,
    MatButtonModule,
    RouterModule,
  ],
  template: `
    @if (user$ | async; as user) {
      <div class="user-info-container">
        <div class="user-details">
          <h3>User Information</h3>
          @if (showDebugInfo) {
            <p class="user-id">
              <strong>ID:</strong> {{ truncateId(user.uid) }}
            </p>
          }
          @if (showDebugInfo) {
            <p>
              <strong>Email Verified:</strong>
              {{ user.emailVerified ? 'Yes' : 'No' }}
            </p>
          }
        </div>
        @if (showDebugInfo) {
          <mat-divider></mat-divider>
        }
        <div class="role-section">
          <h3>User Role</h3>
          @if (userState$ | async; as state) {
            <div>
              @if (state.isAdmin) {
                <p><strong>Admin:</strong> Yes</p>
              }
              @if (!state.isAdmin) {
                <p><strong>Admin:</strong> No</p>
              }
              @if (state.organisationId && showDebugInfo) {
                <p><strong>Organisation:</strong> {{ state.organisationId }}</p>
              }
              @if (!state.organisationId && showDebugInfo) {
                <p><strong>Organisation:</strong> None</p>
              }
              @if (state.role) {
                <p><strong>Role:</strong> {{ state.role }}</p>
              }
              @if (!state.role) {
                <p><strong>Role:</strong> None</p>
              }
              @if (state.isAdmin) {
                <div class="admin-actions">
                  <a mat-button color="primary" routerLink="/users">
                    <mat-icon>people</mat-icon> Manage Users
                  </a>
                  <a mat-button color="primary" routerLink="/organisations">
                    <mat-icon>business</mat-icon> Manage Organisations
                  </a>
                </div>
              }
            </div>
          }
        </div>
        @if (showDebugInfo) {
          <mat-divider></mat-divider>
        }
        @if (showDebugInfo) {
          <div class="claims-section">
            <h3>User Claims</h3>
            @if (userClaims$ | async; as claims) {
              <div>
                <mat-list dense>
                  @for (claim of claims; track claim) {
                    <mat-list-item>
                      <span matListItemTitle>{{ claim.key }}</span>
                      <span matListItemLine class="claim-value">{{
                        formatClaimValue(claim.value)
                      }}</span>
                    </mat-list-item>
                  }
                  @if (claims.length === 0) {
                    <mat-list-item>
                      <span matListItemTitle>No custom claims found</span>
                    </mat-list-item>
                  }
                </mat-list>
              </div>
            }
          </div>
        }
        @if (showDebugInfo) {
          <div class="actions-section">
            <button mat-button color="primary" (click)="refreshToken()">
              <mat-icon>refresh</mat-icon> Refresh Token
            </button>
          </div>
        }
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .user-info-container {
        padding: 8px 0;
        max-width: 400px;
      }

      .user-details,
      .role-section {
        padding: 0 16px 16px;
      }

      .claims-section {
        padding: 0 16px;
      }

      .actions-section {
        padding: 16px;
        display: flex;
        justify-content: center;
      }

      .admin-actions {
        margin-top: 16px;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      h3 {
        margin: 16px 0 8px;
        font-weight: 500;
        font-size: 16px;
      }

      p {
        margin: 4px 0;
        font-size: 14px;
      }

      .user-id {
        word-break: break-all;
      }

      .claim-value {
        color: var(--color-fg-muted);
        font-size: 12px;
      }

      mat-divider {
        margin: 8px 0;
      }

      ::ng-deep .mat-mdc-list-item {
        height: auto !important;
      }
    `,
  ],
})
export class UserInfoComponent implements OnInit {
  private authService = inject(AuthService);
  private userStateService = inject(UserStateService);

  user$ = this.userStateService.user$;
  userState$ = this.userStateService.state$;
  showDebugInfo = environment.showDebugInfo;

  userClaims$: Observable<{ key: string; value: any }[]> = this.user$.pipe(
    switchMap((user) => {
      if (!user) return of([]);

      return from(this.authService.getUserRoles(user)).pipe(
        map((claims) => {
          // Filter out standard claims and format for display
          const standardClaims = [
            'aud',
            'auth_time',
            'exp',
            'iat',
            'iss',
            'sub',
            'user_id',
            'email',
          ];
          return Object.entries(claims)
            .filter(([key]) => !standardClaims.includes(key))
            .map(([key, value]) => ({ key, value }));
        }),
      );
    }),
  );

  ngOnInit(): void {
    // Log current user state on component init only if debug is enabled
    if (this.showDebugInfo) {
      combineLatest([this.user$, this.userState$]).subscribe(
        ([user, state]) => {
          if (user) {
            console.log('Current user:', user);
            console.log('User state:', {
              isAdmin: state.isAdmin,
              organisationId: state.organisationId,
              role: state.role,
            });
          }
        },
      );
    }
  }

  truncateId(id: string): string {
    if (id.length > 20) {
      return id.substring(0, 10) + '...' + id.substring(id.length - 10);
    }
    return id;
  }

  formatClaimValue(value: any): string {
    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }
    return JSON.stringify(value);
  }

  refreshToken(): void {
    this.userStateService
      .refreshUserState()
      .then(() => {
        if (this.showDebugInfo) {
          console.log('Token refreshed successfully');
        }
      })
      .catch((error) => {
        console.error('Error refreshing token:', error);
      });
  }
}
